'use client';

import { useState, useTransition } from 'react';
import { Search, UserRoundCog } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { StorageBuckets } from '@kit/shared/storage';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@kit/ui/command';
import {
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@kit/ui/dropdown-menu';
import { MediaAvatar } from '@kit/ui/media-avatar';
import { toast } from '@kit/ui/sonner';
import { Spinner } from '@kit/ui/spinner';

import { useUserSearch } from '../_lib/hooks/use-user-search';
import {
  quickImpersonateUserAction,
  type ImpersonateUserResult,
} from '../_lib/server/quick-impersonate-actions';

/**
 * Super-admin quick impersonation, folded into the account dropdown as a
 * submenu (2026-07-05 chrome consolidation — it lived in the sidebar footer
 * before). Uses the shadcn combobox-in-dropdown composition: a cmdk Command
 * inside DropdownMenuSubContent, searching users server-side and generating
 * a single-use magic link that is copied to the clipboard.
 */
export function ImpersonateSubMenu() {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [isPending, startTransition] = useTransition();

  const { users, isLoading } = useUserSearch({
    query: searchQuery,
    enabled: true,
  });

  const getInitials = (user: ImpersonateUserResult) => {
    const first = user.first_name?.[0] || '';
    const last = user.last_name?.[0] || '';
    return (
      (first + last).toUpperCase() || (user.email?.[0] || '?').toUpperCase()
    );
  };

  const getDisplayName = (user: ImpersonateUserResult) => {
    if (user.first_name || user.last_name) {
      return `${user.first_name || ''} ${user.last_name || ''}`.trim();
    }
    return user.email;
  };

  const handleSelect = (user: ImpersonateUserResult) => {
    startTransition(async () => {
      const result = await quickImpersonateUserAction(user.id);

      if (result.success && result.data?.actionLink) {
        const { actionLink, brand } = result.data;
        const displayName = getDisplayName(user);
        const brandLabel = brand === 'ballee' ? 'Ballee' : 'Flow';

        try {
          await navigator.clipboard.writeText(actionLink);
          toast.success(
            t('home:quickImpersonate.magicLinkCopied', { name: displayName }),
            {
              description: t(
                'home:quickImpersonate.magicLinkCopiedDescription',
                { brand: brandLabel },
              ),
              duration: 8000,
            },
          );
        } catch (err) {
          // Clipboard API can fail in non-secure contexts or when denied —
          // surface the link so the admin can copy it manually.
          console.error('Clipboard write failed', err);
          toast.error(t('home:quickImpersonate.copyFailed'), {
            description: actionLink,
            duration: 30000,
          });
        }
      } else {
        toast.error(result.error || t('home:quickImpersonate.generateFailed'));
      }
    });

    setSearchQuery('');
  };

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger className="cursor-pointer">
        <span className="flex items-center space-x-2">
          {isPending ? (
            <Spinner className="h-5 w-5" />
          ) : (
            <UserRoundCog className="h-5 w-5 text-orange-600 dark:text-orange-400" />
          )}
          <span className="text-orange-700 dark:text-orange-400">
            {t('admin:quickImpersonate.button', 'Impersonate')}
          </span>
        </span>
      </DropdownMenuSubTrigger>

      <DropdownMenuPortal>
        <DropdownMenuSubContent className="w-[320px] p-0">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder={t(
                'admin:quickImpersonate.searchPlaceholder',
                'Search by name or email...',
              )}
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            <CommandList>
              {searchQuery.length < 2 && (
                <div className="text-muted-foreground flex flex-col items-center gap-2 py-6 text-center text-sm">
                  <Search className="text-muted-foreground/50 size-8" />
                  <span>
                    {t(
                      'admin:quickImpersonate.searchHint',
                      'Type at least 2 characters to search',
                    )}
                  </span>
                </div>
              )}

              {isLoading && searchQuery.length >= 2 && (
                <div className="flex items-center justify-center py-6">
                  <Spinner className="size-5" />
                </div>
              )}

              {!isLoading && searchQuery.length >= 2 && users.length === 0 && (
                <CommandEmpty>
                  {t('admin:quickImpersonate.noResults', 'No users found')}
                </CommandEmpty>
              )}

              {!isLoading && users.length > 0 && (
                <CommandGroup>
                  {users.map((user) => (
                    <CommandItem
                      key={user.id}
                      value={user.id}
                      onSelect={() => handleSelect(user)}
                      className="cursor-pointer px-3 py-2"
                    >
                      <div className="flex items-center gap-3">
                        <MediaAvatar
                          className="size-8"
                          media={{
                            bucket: StorageBuckets.DANCER_MEDIA,
                            path: user.profile_photo_url,
                            visibility: 'public',
                          }}
                          alt={getDisplayName(user)}
                          fallback={getInitials(user)}
                          fallbackClassName="text-xs"
                        />
                        <div className="flex flex-col overflow-hidden">
                          <span className="truncate text-sm font-medium">
                            {getDisplayName(user)}
                          </span>
                          <span className="text-muted-foreground truncate text-xs">
                            {user.email}
                          </span>
                        </div>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </DropdownMenuSubContent>
      </DropdownMenuPortal>
    </DropdownMenuSub>
  );
}
